import { useState, useEffect, useCallback } from 'react';
import { getProducts, getProductById, createProduct, updateProduct, deleteProduct } from '../../../../ApiCalls/mantisFlowsProducts';
import { getInventoryItems } from '../../../../ApiCalls/mantisFlowsInventory';
import { getCategories } from '../../../../ApiCalls/mantisFlowsCategories';

export const useProducts = (clientId) => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableItems, setAvailableItems] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Simple function to load products
  const loadProducts = useCallback(async () => {
    if (!clientId) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await getProducts(clientId, {});
      console.log(response)
      if (response.success) {
        const productsData = Array.isArray(response.data.data) ? response.data.data : [];
        setProducts(productsData);
        setTotalItems(response.data.count || productsData.length || 0);
      } else {
        setError(response.message || 'Error al cargar productos');
        setProducts([]);
        setTotalItems(0);
      }
    } catch (err) {
      setError(err.message || 'Error de conexión');
      setProducts([]);
      setTotalItems(0);
      console.error('Error loading products:', err);
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  // Fetch available items for product association
  const fetchAvailableItems = useCallback(async (itemFilters = {}) => {
    if (!clientId) return;
    
    try {
      // Solo buscar cuando hay un filtro de búsqueda para evitar cargar todos los items
      if (!itemFilters.name && !itemFilters.sku && Object.keys(itemFilters).length === 0) {
        setAvailableItems([]);
        return;
      }

      const response = await getInventoryItems(clientId, itemFilters);
      
      // Manejar la estructura específica de respuesta del inventario
      if (response && response.success && Array.isArray(response.data)) {
        // Limitar a los primeros 50 items para mejor performance
        const limitedItems = response.data.slice(0, 50);
        setAvailableItems(limitedItems);
      } else if (response && Array.isArray(response)) {
        // Fallback si la respuesta es directamente un array
        const limitedItems = response.slice(0, 50);
        setAvailableItems(limitedItems);
      } else {
        setAvailableItems([]);
      }
    } catch (err) {
      console.error('Error fetching inventory items:', err);
      setAvailableItems([]);
    }
  }, [clientId]);

  // Create new product
  const handleCreateProduct = useCallback(async (productData) => {
    if (!clientId) throw new Error('Client ID is required');
    
    try {
      setIsLoading(true);
      const response = await createProduct(clientId, productData);
      
      if (response.success) {
        // Reload products
        await loadProducts();
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Error al crear producto');
      }
    } catch (err) {
      const error = err.message || 'Error al crear producto';
      setError(error);
      throw new Error(error);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, loadProducts]);

  // Update existing product
  const handleUpdateProduct = useCallback(async (productId, productData) => {
    if (!clientId || !productId) throw new Error('Client ID and Product ID are required');
    
    try {
      setIsLoading(true);
      const response = await updateProduct(clientId, productId, productData);
      
      if (response.success) {
        // Reload products
        await loadProducts();
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Error al actualizar producto');
      }
    } catch (err) {
      const error = err.message || 'Error al actualizar producto';
      setError(error);
      throw new Error(error);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, loadProducts]);

  // Delete product
  const handleDeleteProduct = useCallback(async (productId, additionalData = {}) => {
    if (!clientId || !productId) throw new Error('Client ID and Product ID are required');
    
    try {
      setIsLoading(true);
      const response = await deleteProduct(clientId, productId, additionalData);
      
      if (response.success) {
        // Reload products
        await loadProducts();
        return { success: true };
      } else {
        throw new Error(response.message || 'Error al eliminar producto');
      }
    } catch (err) {
      const error = err.message || 'Error al eliminar producto';
      setError(error);
      throw new Error(error);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, loadProducts]);

  // Get product by ID
  const getProduct = useCallback(async (productId) => {
    if (!clientId || !productId) return null;
    
    try {
      const response = await getProductById(clientId, productId);
      if (response.success) {
        return response.data;
      }
      return null;
    } catch (err) {
      console.error('Error getting product:', err);
      return null;
    }
  }, [clientId]);

  // Pagination helpers
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = Array.isArray(products) ? products.slice(startIndex, endIndex) : [];

  const goToPage = useCallback((page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  }, [totalPages]);

  const changeItemsPerPage = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);


  // Initialize - load products only once
  useEffect(() => {
    const initializeProducts = async () => {
      if (!clientId) return;
      
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await getProducts(clientId, {});
        
        if (response.success) {
          const productsData = Array.isArray(response.data.data) ? response.data.data : [];
          setProducts(productsData);
          setTotalItems(response.data.count || productsData.length || 0);
        } else {
          setError(response.message || 'Error al cargar productos');
          setProducts([]);
          setTotalItems(0);
        }
      } catch (err) {
        setError(err.message || 'Error de conexión');
        setProducts([]);
        setTotalItems(0);
        console.error('Error initializing products:', err);
      } finally {
        setIsLoading(false);
      }
    };

    const initializeCategories = async () => {
      const response = await getCategories(clientId);
      if (response.success) {
        setCategories(response.data);
      }
    };

    if (clientId) {
      initializeCategories();
      initializeProducts();
    }
  }, [clientId]);

  return {
    // Data
    products: currentProducts,
    allProducts: products,
    availableItems,
    categories,
    
    // State
    isLoading,
    error,
    
    // Pagination
    currentPage,
    totalPages,
    itemsPerPage,
    totalItems,
    startIndex: startIndex + 1,
    endIndex: Math.min(endIndex, totalItems),
    
    // Actions
    createProduct: handleCreateProduct,
    updateProduct: handleUpdateProduct,
    deleteProduct: handleDeleteProduct,
    getProduct,
    fetchAvailableItems,
    refresh: loadProducts,
    goToPage,
    changeItemsPerPage,
    
    // Helpers
    hasNextPage: currentPage < totalPages,
    hasPrevPage: currentPage > 1
  };
}; 